import type {
  MonthlyReconciliationReport,
  RegulatoryExtractResponse,
} from '@nadoutong/contracts';
import { useQuery } from '@tanstack/react-query';
import type { UseQueryResult } from '@tanstack/react-query';
import { useSession } from '../hooks/useSession.tsx';
import { opsApiRequest } from './client.ts';

/**
 * `ops-api /v1/ops/reports/*` thin client + React Query hook. Epic #109
 * P3.14.
 *
 * The CSV download route is NOT wrapped in a hook — fetching a Blob via
 * React Query would cache binary payloads in memory indefinitely. The
 * page component builds a direct anchor with the session cookie-scoped
 * URL and triggers a download.
 */

const BASE = '/v1/ops/reports';

export async function getMonthlyReconciliation(
  month: string,
  fetchImpl: typeof fetch,
  signal?: AbortSignal,
): Promise<MonthlyReconciliationReport> {
  return opsApiRequest<MonthlyReconciliationReport>(
    `${BASE}/monthly-reconciliation?month=${encodeURIComponent(month)}`,
    { signal },
    fetchImpl,
  );
}

export function monthlyReconciliationCsvUrl(month: string): string {
  // Relative URL; the session cookie is scoped to the ops-api domain and
  // the browser attaches it automatically on the download request.
  return `${BASE}/monthly-reconciliation.csv?month=${encodeURIComponent(month)}`;
}

export function reportsQueryKey(month: string): readonly ['reports', 'monthly', string] {
  return ['reports', 'monthly', month] as const;
}

export function useMonthlyReconciliation(
  month: string,
): UseQueryResult<MonthlyReconciliationReport> {
  const { fetchImpl } = useSession();
  return useQuery({
    queryKey: reportsQueryKey(month),
    queryFn: ({ signal }) => getMonthlyReconciliation(month, fetchImpl, signal),
    // Month reports don't mutate after the fact; keep for the whole
    // session so operators can tab between months without refetching.
    staleTime: 5 * 60 * 1000,
  });
}

// ─── regulatory extract (P3.15) ──────────────────────────────────────

export async function getRegulatoryExtract(
  month: string,
  fetchImpl: typeof fetch,
  signal?: AbortSignal,
): Promise<RegulatoryExtractResponse> {
  return opsApiRequest<RegulatoryExtractResponse>(
    `${BASE}/regulatory-extract?month=${encodeURIComponent(month)}`,
    { signal },
    fetchImpl,
  );
}

export function regulatoryExtractCsvUrl(month: string): string {
  return `${BASE}/regulatory-extract.csv?month=${encodeURIComponent(month)}`;
}

export function regulatoryQueryKey(
  month: string,
): readonly ['reports', 'regulatory', string] {
  return ['reports', 'regulatory', month] as const;
}

export function useRegulatoryExtract(
  month: string,
): UseQueryResult<RegulatoryExtractResponse> {
  const { fetchImpl } = useSession();
  return useQuery({
    queryKey: regulatoryQueryKey(month),
    queryFn: ({ signal }) => getRegulatoryExtract(month, fetchImpl, signal),
    staleTime: 5 * 60 * 1000,
  });
}
